import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {MatDialog} from '@angular/material/dialog';
import {FeeService} from '../../../services/fee/fee.service';
import {
  AuditLogPage,
  CommissionFee, CommissionFeeGet,
  CommissionFeePeriodPriceWithLevel,
  CommissionLevel,
  CommissionType,
  PaymentTerm,
  PaymentTermType, TranslationValue
} from '../../../generated/sales-terminal-client';
import {finalize} from 'rxjs/operators';
import {debounceTime, forkJoin, Observable, Subject, Subscription} from 'rxjs';
import {AuthService} from '../../../services/auth/auth.service';
import {Privileges} from '../../../modules/admin/privileges/privileges.constants';
import {SnackService} from '../../../services/snack/snack.service';
import {UtilService} from '../../../services/util/util.service';
import {TranslateService} from '@ngx-translate/core';
import {CommissionFeeDialogComponent} from '../commission-fee-dialog/commission-fee-dialog.component';

export interface SortedCommissionFee extends CommissionFeeGet {
  currentPeriodPrice?: CommissionFeePeriodPriceWithLevel;
  futurePeriodPrices?: CommissionFeePeriodPriceWithLevel[];
  pastPeriodPrices?: CommissionFeePeriodPriceWithLevel[];
  sameLevelPeriodPrices?: CommissionFeePeriodPriceWithLevel[];
  showMore?: boolean;
}

@Component({
  selector: 'app-commission-fees',
  templateUrl: './commission-fees.component.html',
  styleUrls: ['./commission-fees.component.scss']
})
export class CommissionFeesComponent implements OnInit, OnDestroy {
  @Input() public baseId: number; // Empty if super centre, legalPersonId if legal person and eventId if event
  @Input() public baseName: TranslationValue[] | string; // Event or Legal name
  @Input() public commissionLevel: CommissionLevel = CommissionLevel.SuperCentre;
  @Input() public manageDisabled: boolean;
  public commissionFees: SortedCommissionFee[] = [];
  public paymentTerm: PaymentTerm;
  public isLoading: boolean;
  public auditLogPage: AuditLogPage;
  public auditPageNumber: number = 1;
  private auditPageSize: number = 30;
  public numberOfDaysSubject: Subject<number> = new Subject<number>();
  private _originalNumberOfDays: number;
  private _subs: Subscription[] = [];
  private loadedFeeIds: number[] = [];
  public displayPastFees: boolean = false;

  constructor(private dialog: MatDialog,
              private feeService: FeeService,
              public authService: AuthService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private utilService: UtilService) {}

  public ngOnInit(): void {
    this.loadOverview();
    this._subs.push(
      this.numberOfDaysSubject.pipe(debounceTime(1000)).subscribe((numberOfDays: number) => {
        this.updatePaymentTerm(numberOfDays);
      })
    );
  }
  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
    this._subs = [];
  }

  private loadOverview(): void {
    this.isLoading = true;
    switch (this.commissionLevel) {
      case CommissionLevel.SuperCentre:
        this.feeService.getSuperCentreCommissionFees().pipe(
          finalize(() => this.isLoading = false)
        ).subscribe((commissionFees: CommissionFeeGet[]) =>
          this.prepareCommissionFeeResults(commissionFees));
        break;
      case CommissionLevel.Legal:
        const legalPaymentTermRequest: Observable<PaymentTerm> = this.feeService.getLegalPersonCommissionFeePaymentTerm(this.baseId);
        const legalFeesRequest: Observable<CommissionFee[]> = this.feeService.getLegalPersonCommissionFees(this.baseId);
        forkJoin([legalPaymentTermRequest, legalFeesRequest]).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe((results: [PaymentTerm, CommissionFeeGet[]]) => {
          this._originalNumberOfDays = results[0]?.numberOfDays;
          this.paymentTerm = results[0];
          this.prepareCommissionFeeResults(results[1]);
        });
        break;
      case CommissionLevel.Event:
        const eventPaymentTermRequest: Observable<PaymentTerm> = this.feeService.getEventCommissionFeePaymentTerm(this.baseId);
        const eventFeesRequest: Observable<CommissionFee[]> = this.feeService.getEventCommissionFees(this.baseId);
        forkJoin([eventPaymentTermRequest, eventFeesRequest]).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe((results: [PaymentTerm, CommissionFeeGet[]]) => {
          this._originalNumberOfDays = results[0]?.numberOfDays;
          this.paymentTerm = results[0];
          this.prepareCommissionFeeResults(results[1]);
        });
        break;
      default:
        this.isLoading = false;
        break;
    }
  }

  private prepareCommissionFeeResults(results: CommissionFeeGet[]): void {
    this.commissionFees = [
      {type: CommissionType.Webshop, comment: null, periodPrices: [], level: CommissionLevel.SuperCentre},
      {type: CommissionType.Widget, comment: null, periodPrices: [], level: CommissionLevel.SuperCentre}
    ];
    this.loadedFeeIds = results.map((fee: CommissionFeeGet) => {
      if (fee.type === CommissionType.Webshop) {
        this.commissionFees[0] = this.feeService.sortFeePeriods(fee, this.commissionLevel);
      } else if (fee.type === CommissionType.Widget) {
        this.commissionFees[1] = this.feeService.sortFeePeriods(fee, this.commissionLevel);
      }
      return fee.id;
    });
    this.getAuditLogs();
  }

  public get anyFeeMissing(): boolean {
    return this.commissionFees.some((fee: SortedCommissionFee) => !fee.id);
  }

  public openCommissionFeeModal(commissionFee: SortedCommissionFee): void {
    const dialogRef: MatDialogRef<CommissionFeeDialogComponent> = this.dialog.open(CommissionFeeDialogComponent, {
      width: '60%',
      data: {
        targetCommissionLevel: this.commissionLevel,
        commissionFee: commissionFee,
        baseId: this.baseId,
        baseName: this.baseName
      }
    });
    dialogRef.afterClosed().subscribe((result: SortedCommissionFee) => {
      this.loadOverview();
    });
  }

  // Temporary default for number of days is 7
  public updatePaymentTerm(numberOfDays: number = 7): void {
    this.isLoading = true;
    if (this.paymentTerm.type === PaymentTermType.Netting) {
      this.paymentTerm.numberOfDays = null;
    } else {
      this.paymentTerm.numberOfDays = numberOfDays;
    }
    if (this.commissionLevel === CommissionLevel.Legal) {
      this.feeService.updateLegalPersonCommissionFeePaymentTerm(this.baseId, this.paymentTerm).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe((result: PaymentTerm) => this.finishPaymentTermUpdate(result));
    } else {
      this.feeService.updateEventCommissionFeePaymentTerm(this.baseId, this.paymentTerm).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe((result: PaymentTerm) => this.finishPaymentTermUpdate(result));
    }
  }
  private finishPaymentTermUpdate(paymentTerm: PaymentTerm): void {
    this._originalNumberOfDays = paymentTerm?.numberOfDays;
    this.paymentTerm = paymentTerm;
    this.snackService.showSuccess(this.translateService.instant('general.api_fees_payment_term_successfully_saved'));
  }
  public updateNumberOfDays(valid: boolean): void {
    if (!this._originalNumberOfDays) return;
    if (valid && this.paymentTerm.numberOfDays > 0 && this._originalNumberOfDays !== this.paymentTerm.numberOfDays) {
      this.numberOfDaysSubject.next(this.paymentTerm.numberOfDays);
    }
  }

  public getAuditLogs(): void {
    if (this.commissionLevel === CommissionLevel.SuperCentre && this.canSeeAuditLogs) {
      this.feeService.getCommissionFeeSuperCentreAuditLogsByCommissionIds(this.loadedFeeIds, this.auditPageNumber, this.auditPageSize)
        .subscribe((auditPage: AuditLogPage) => {
          this.auditLogPage = auditPage;
        });
    }
  }

  public get canSeeAuditLogs(): boolean {
    return this.authService.hasAuditGetPrivilege && this.authService.user.isSuperCentre;
  }

  public get canManageFees(): boolean {
    return this.authService.checkPrivileges([Privileges.FEE_MANAGE]) && !this.manageDisabled;
  }

  protected readonly PaymentTermType: {Netting: PaymentTermType, Transfer: PaymentTermType} = PaymentTermType;
  protected readonly CommissionLevel: {SuperCentre: CommissionLevel, Legal: CommissionLevel, Event: CommissionLevel} = CommissionLevel;
}
